import React from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { Upload, X } from 'lucide-react';
import { useScrollLock } from '../hooks/useScrollLock';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const InfoSection = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const InfoText = styled.div`
  font-size: 0.875rem;
  color: #374151;
  margin-bottom: 0.25rem;
`;

const FileCount = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const ProgressSection = styled.div`
  margin-bottom: 1rem;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 12px;
  background: #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
`;

const ProgressBar = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 6px;
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`;

const StatusMessage = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.75rem;
  text-align: center;
  min-height: 1.25rem;
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const FileUploadProgressModal = ({ 
  isOpen, 
  onClose,
  totalFiles = 0,
  uploadProgress = 0,
  status = 'Preparing upload...',
  uploadedBytes = 0,
  totalBytes = 0
}) => {
  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  if (!isOpen) return null;

  // Calculate size info if available
  const sizeInfo = totalBytes > 0
    ? `(${(uploadedBytes / 1024 / 1024).toFixed(2)} MB / ${(totalBytes / 1024 / 1024).toFixed(2)} MB)`
    : '';

  const modalContent = (
    <ModalOverlay>
      <ModalContent>
        <Header>
          <Title>
            <Upload size={24} color="#3b82f6" />
            Uploading Files
          </Title>
          {onClose && (
            <CloseButton onClick={onClose} title="Close">
              <X size={20} />
            </CloseButton>
          )}
        </Header>
        
        <InfoSection>
          <InfoText>
            Uploading {totalFiles} file{totalFiles !== 1 ? 's' : ''} for processing
          </InfoText>
          {totalBytes > 0 && (
            <FileCount>
              {sizeInfo}
            </FileCount>
          )}
        </InfoSection>
        
        <ProgressSection>
          <ProgressLabel>
            <span>Upload Progress</span>
            <span>{Math.round(uploadProgress)}%</span>
          </ProgressLabel>
          <ProgressBarContainer>
            <ProgressBar progress={uploadProgress} />
          </ProgressBarContainer>
        </ProgressSection>
        
        <StatusMessage>
          {status && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              <Spinner />
              <span>{status}</span>
            </div>
          )}
        </StatusMessage>
      </ModalContent>
    </ModalOverlay>
  );

  // Render modal outside of the component hierarchy using portal
  return createPortal(modalContent, document.body);
};

export default FileUploadProgressModal;

